import {
  OktonDateFilter,
  OktonLoadingCard,
  OktonErrorCard,
  OktonSearchSelect,
  OktonPageHeader,
  OktonSearchFilter,
  OktonSegmentedFilter,
  OktonDataGrid,
  OktonEmptyState,
  OktonConfirmDialog,
  ColunaGrid,
  useGrid,
  TAMANHOS_PAGINA,
} from '@okton/platform-ui';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { BadgeCheck, PiggyBank, Plus, Trash2, Undo2 } from 'lucide-react';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import BankAppPage from '../../layout/BankAppPage';
import { useContasBancarias } from '../../services/hooks/useContasBancarias';
import {
  Deposito,
  podeCompensar,
  podeDescompensar,
  STATUS_DEPOSITO,
  useDeleteDeposito,
  useDepositos,
  useDescompensarDeposito,
} from '../../services/hooks/useDepositos';
import { formatCustomDate } from '../../utils/formatCustomDate';
import formatValue from '../../utils/formatValue';
import { FiltroData, setaFiltroAnual } from '../../utils/setaFiltroDiario';
import { colunasDataGrid } from '../../utils/colunasDataGrid';

const COLUNAS: ColunaGrid<Deposito>[] = [
  { chave: 'data_dep', titulo: 'Data', valor: d => d.data_dep, tipo: 'data' },
  { chave: 'descricao_cts', titulo: 'Conta', valor: d => d.descricao_cts },
  {
    chave: 'nomemoedabanco_dep',
    titulo: 'Moeda Banco',
    valor: d => d.nomemoedabanco_dep,
  },
  { chave: 'nome_moe', titulo: 'Moeda Depositada', valor: d => d.nome_moe },
  { chave: 'envelope_dep', titulo: 'Envelope', valor: d => d.envelope_dep },
  {
    chave: 'status_dep',
    titulo: 'Situação',
    // Ordena e busca pelo nome que está na tela, não pelo código.
    valor: d => STATUS_DEPOSITO[d.status_dep] ?? d.status_dep,
  },
  {
    chave: 'datacompensacao_dep',
    titulo: 'Compensação',
    valor: d => d.datacompensacao_dep,
    tipo: 'data',
  },
  { chave: 'nome_pct', titulo: 'Plano de Contas', valor: d => d.nome_pct },
  {
    chave: 'conciliado_dep',
    titulo: 'Status',
    valor: d => (d.conciliado_dep !== '0' ? 'Conciliado' : 'Pendente'),
  },
  {
    chave: 'valor_dep',
    titulo: 'Valor',
    valor: d => Number(d.valor_dep),
    tipo: 'numero',
    alinhamento: 'right',
  },
];

const Depositos = () => {
  const history = useHistory();
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  const [filtroData, setFiltroData] = useState<FiltroData>(setaFiltroAnual());
  const [filterAccount, setFilterAccount] = useState('');
  const [filterStatus, setFilterStatus] = useState<
    'all' | 'pending' | 'reconciled'
  >('all');

  const { data: contas = [] } = useContasBancarias();
  const {
    data: depositos = [],
    isLoading,
    isError,
    refetch,
  } = useDepositos(
    filterAccount || undefined,
    filtroData[1],
    filtroData[2],
    filterStatus,
  );
  const deleteMutation = useDeleteDeposito();
  const toast = useToast();
  const descompensarMutation = useDescompensarDeposito();
  const [descompensando, setDescompensando] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);
  const [itemExcluir, setItemExcluir] = useState<string | null>(null);
  const [itemDescompensar, setItemDescompensar] = useState<Deposito | null>(
    null,
  );

  const [busca, setBusca] = useState('');
  const {
    ordenacao,
    alternarOrdenacao,
    itens: visiveis,
    filtrados,
    pagina,
    setPagina,
    tamanhoPagina,
    setTamanhoPagina,
  } = useGrid(depositos, COLUNAS, busca);

  const remove = async () => {
    if (!itemExcluir) return;
    setExcluindo(itemExcluir);
    try {
      await deleteMutation.mutateAsync(itemExcluir);
      setItemExcluir(null);
      refetch();
    } catch (err) {
      toast({
        title:
          err instanceof Error ? err.message : 'Erro ao excluir o Depósito.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setExcluindo(null);
    }
  };

  // Desfazer não abre tela: não há o que informar, só o que confirmar. E vale
  // confirmar, porque a data em que o banco compensou se perde no caminho.
  const descompensar = async () => {
    if (!itemDescompensar) return;
    setDescompensando(itemDescompensar.id_dep);
    try {
      await descompensarMutation.mutateAsync(itemDescompensar.id_dep);
      setItemDescompensar(null);
    } catch (err) {
      toast({
        title:
          err instanceof Error
            ? err.message
            : 'Erro ao desfazer a compensação.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setDescompensando(null);
    }
  };

  const dataCompensacao = itemDescompensar?.datacompensacao_dep
    ? ` de ${formatCustomDate(
        itemDescompensar.datacompensacao_dep.slice(0, 10),
        'yyyy-MM-dd',
        'dd/MM/yyyy',
      )}`
    : '';

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Depósitos"
        subtitle="Entradas na Conta Bancária"
        actions={
          <>
            <OktonDateFilter
              value={{
                preset: filtroData[0],
                start: filtroData[1],
                end: filtroData[2],
              }}
              onChange={next =>
                setFiltroData([next.preset, next.start, next.end])
              }
            />
          </>
        }
      />

      <Flex minH="0" w="100%" my="3" mt="4" gridGap={2} flexWrap="wrap">
        <OktonSegmentedFilter<typeof filterStatus>
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: 'all', label: 'Todos' },
            { value: 'pending', label: 'Pendentes' },
            { value: 'reconciled', label: 'Conciliados' },
          ]}
        />
        <OktonSearchFilter
          value={busca}
          onChange={setBusca}
          placeholder="Buscar por conta, forma, envelope..."
        />
        <Flex
          borderWidth="1px"
          ml="auto"
          p="2"
          rounded="2xl"
          bg={bg}
          align="center"
          gridGap={2}
        >
          <Button
            leftIcon={<Plus size={18} />}
            colorScheme="blue"
            onClick={() => history.push('/depositos/novo')}
            minW="150px"
          >
            Novo Depósito
          </Button>
          <OktonSearchSelect
            name="filtroconta"
            placeholder="Todas as contas"
            value={filterAccount}
            onChange={valor => setFilterAccount(valor)}
            options={[
              ...contas.map(c => ({
                value: c.id_cts,
                label: c.descricao_cts,
              })),
            ]}
          />
        </Flex>
      </Flex>

      {isLoading ? (
        <Flex mt="1">
          <OktonLoadingCard h="200px" />
        </Flex>
      ) : isError ? (
        <Flex mt="1">
          <OktonErrorCard h="200px" onRetry={refetch} />
        </Flex>
      ) : (
        <Box bg={bg} mt="1" rounded="2xl">
          {filtrados.length === 0 ? (
            <OktonEmptyState
              icon={PiggyBank}
              title={
                busca
                  ? 'Nada encontrado para a busca'
                  : 'Nenhum depósito no período'
              }
            />
          ) : (
            <OktonDataGrid
              rows={visiveis}
              columns={[
                ...colunasDataGrid(COLUNAS, ordenacao, alternarOrdenacao, {
                  data_dep: s => (
                    <Box color="gray.600" whiteSpace="nowrap">
                      {formatCustomDate(
                        s.data_dep.slice(0, 10),
                        'yyyy-MM-dd',
                        'dd/MM/yyyy',
                      )}
                    </Box>
                  ),
                  descricao_cts: s => (
                    <Box color={color} fontWeight="bold">
                      {s.descricao_cts ?? '—'}
                    </Box>
                  ),
                  nomemoedabanco_dep: s => (
                    <Box color="gray.500">{s.nomemoedabanco_dep ?? '—'}</Box>
                  ),
                  nome_moe: s => (
                    <Box color="gray.500">{s.nome_moe ?? '—'}</Box>
                  ),
                  envelope_dep: s => (
                    <Box color="gray.500">{s.envelope_dep ?? '—'}</Box>
                  ),
                  status_dep: s => (
                    <>
                      <Badge
                        colorScheme={s.status_dep === '1' ? 'orange' : 'green'}
                        borderRadius="full"
                        px="2"
                      >
                        {STATUS_DEPOSITO[s.status_dep] ?? s.status_dep}
                      </Badge>
                    </>
                  ),
                  /* A data vem do item como "yyyy-MM-dd HH:mm:ss": a hora
                  importa — é o que distingue a compensação do dia. */
                  datacompensacao_dep: s => (
                    <Box color="gray.500" whiteSpace="nowrap">
                      {s.datacompensacao_dep
                        ? `${formatCustomDate(
                            s.datacompensacao_dep.slice(0, 10),
                            'yyyy-MM-dd',
                            'dd/MM/yyyy',
                          )} ${s.datacompensacao_dep.slice(11, 16)}`
                        : '—'}
                    </Box>
                  ),
                  nome_pct: s => (
                    <>
                      {s.nome_pct ? (
                        <Badge colorScheme="blue" borderRadius="full" px="2">
                          {s.nome_pct}
                        </Badge>
                      ) : (
                        <Text color="gray.400">—</Text>
                      )}
                    </>
                  ),
                  conciliado_dep: s => (
                    <>
                      {s.conciliado_dep !== '0' ? (
                        <Badge colorScheme="green" borderRadius="full" px="2">
                          Conciliado
                        </Badge>
                      ) : (
                        <Badge colorScheme="orange" borderRadius="full" px="2">
                          Pendente
                        </Badge>
                      )}
                    </>
                  ),
                  valor_dep: s => (
                    <Box
                      fontWeight="semibold"
                      whiteSpace="nowrap"
                      color="green.600"
                    >
                      {formatValue(Number(s.valor_dep))}
                    </Box>
                  ),
                }),
                {
                  key: 'acoes',
                  header: null,
                  align: 'right',
                  render: s => (
                    <Flex justify="flex-end" gridGap="1">
                      {/* Compensar e desfazer são o mesmo lugar na linha: o
                      Depósito está de um lado ou do outro, nunca nos dois. */}
                      {podeCompensar(s) && (
                        <IconButton
                          aria-label="Compensar"
                          title="Compensar"
                          size="sm"
                          variant="ghost"
                          colorScheme="green"
                          icon={<BadgeCheck size={16} />}
                          onClick={() =>
                            history.push(`/depositos/${s.id_dep}/compensar`)
                          }
                        />
                      )}
                      {podeDescompensar(s) && (
                        <IconButton
                          aria-label="Descompensar"
                          title="Descompensar"
                          size="sm"
                          variant="ghost"
                          colorScheme="orange"
                          icon={<Undo2 size={16} />}
                          isLoading={descompensando === s.id_dep}
                          onClick={() => setItemDescompensar(s)}
                        />
                      )}
                      {s.conciliado_dep === '0' && podeCompensar(s) && (
                        <IconButton
                          aria-label="Excluir"
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          icon={<Trash2 size={16} />}
                          isLoading={excluindo === s.id_dep}
                          onClick={() => setItemExcluir(s.id_dep)}
                        />
                      )}
                    </Flex>
                  ),
                },
              ]}
              getRowId={s => s.id_dep}
              page={pagina + 1}
              pageSize={tamanhoPagina}
              total={filtrados.length}
              pageSizeOptions={TAMANHOS_PAGINA}
              onPageChange={nova => setPagina(nova - 1)}
              onPageSizeChange={setTamanhoPagina}
            />
          )}
        </Box>
      )}
      <OktonConfirmDialog
        isOpen={Boolean(itemExcluir)}
        onClose={() => {
          if (!excluindo) setItemExcluir(null);
        }}
        onConfirm={remove}
        title="Confirmar exclusão"
        description="Excluir este Depósito do IFLEX?"
        confirmLabel="Excluir"
        intent="danger"
        isLoading={Boolean(excluindo)}
      />
      <OktonConfirmDialog
        isOpen={Boolean(itemDescompensar)}
        onClose={() => {
          if (!descompensando) setItemDescompensar(null);
        }}
        onConfirm={descompensar}
        title="Desfazer compensação"
        description={`Desfazer a compensação${dataCompensacao} deste Depósito? Ele volta para Pendente e a data da compensação é apagada. O Caixa não é afetado — compensar nunca o movimentou.`}
        confirmLabel="Desfazer compensação"
        intent="danger"
        isLoading={Boolean(descompensando)}
      />
    </BankAppPage>
  );
};

export default Depositos;
